import Button from 'src/components/Button';
import Card from 'src/components/Card';
import Dropdown from 'src/components/Dropdown';
import DropdownContent from 'src/components/DropdownContent';
import { DropdownItem } from 'src/components/DropdownItem';
import DropdownTrigger from 'src/components/DropdownTrigger';
import ProgressIndicator from 'src/components/ProgressIndicator';
import { Tooltip } from 'src/components/Tooltip';
import {
  useSessionActivityQuery,
  useSessionOutcomeQuery,
  useSessionParticipationQuery,
} from 'src/dictionaries/dictionariesApi';
import { useBreakpoint } from 'src/hooks/useBreakpoint';
import useTimeFormat from 'src/hooks/useTimeFormat';
import { IconTripleDots } from 'src/icons';
import { formatTime } from 'src/logic/formatTime';

import { SessionData } from '../schedule/record-session/CancelSessionModal';
import { InvoiceSession } from './billing.types';
import { BillingActivityField } from './BillingActivityField';
export type BillingSingleSessionProps = InvoiceSession & {
  type: 'awaiting-confirmation' | 'confirmed' | 'cancelled';
  onDelete: (sessionId: string) => void;
  onCancel: (session: SessionData) => void;
  onConfirm: (sessionId: string) => void;
};

const BillingSingleSession = ({
  id,
  startTime,
  finishTime,
  date,
  location,
  type,
  isEditable,
  isRescheduled,
  isRejected,
  canBeDeleted,
  goals,
  note,
  student,
  onDelete,
  onCancel,
  onConfirm,
  isFadSession,
}: BillingSingleSessionProps) => {
  const { formatDate } = useTimeFormat();
  const handleDelete = () => {
    onDelete(id);
  };

  const handleCancel = () => {
    onCancel({
      sessionId: id,
      location,
      startTime,
      finishTime,
      date,
    });
  };

  const handleConfirm = () => {
    onConfirm(id);
  };
  const { isMd } = useBreakpoint('md');
  const { data: sessionActivity } = useSessionActivityQuery();
  const { data: sessionParticipation } = useSessionParticipationQuery();
  const { data: sessionOutcome } = useSessionOutcomeQuery();

  return (
    <Card className="grow-0">
      <div className="flex items-center">
        <div className="flex w-full flex-col gap-1 md:w-auto">
          {!isMd ? (
            <div className="flex w-full items-center justify-between ">
              <div className="flex items-center gap-2">
                <div className="text-neutral-100 typography-heading-md">{formatDate(date)}</div>
                {isRejected && <span className="text-red-500 typography-loud-sm">Rejected</span>}
              </div>
              <Dropdown>
                <DropdownTrigger>
                  <Button
                    preset="tertiary"
                    className="rounded-lg"
                    Icon={IconTripleDots}
                    size="xs"
                  />
                </DropdownTrigger>
                <DropdownContent align="end" side="bottom">
                  {!isRescheduled && (
                    <Tooltip
                      side="bottom"
                      content={isFadSession ? 'FAD session cannot be canceled or deleted.' : ''}
                      delayDuration={0}
                    >
                      <span>
                        <DropdownItem handleClick={handleCancel} disabled={isFadSession}>
                          Cancel session
                        </DropdownItem>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip
                    side="bottom"
                    content={
                      !canBeDeleted && !isFadSession
                        ? 'Scheduled sessions cannot be deleted; cancel and/or reschedule as needed.'
                        : isFadSession
                        ? 'FAD session cannot be canceled or deleted.'
                        : ''
                    }
                    delayDuration={0}
                  >
                    <span>
                      <DropdownItem
                        disabled={!canBeDeleted || isFadSession}
                        handleClick={handleDelete}
                      >
                        Delete session
                      </DropdownItem>
                    </span>
                  </Tooltip>
                </DropdownContent>
              </Dropdown>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="text-neutral-100 typography-heading-md">{formatDate(date)}</div>
              {isRejected && (
                <p className="rounded bg-primary-900 px-2 py-0.5 text-primary-500 typography-loud-xs">
                  Rejected
                </p>
              )}
            </div>
          )}
          <div className="flex h-8 items-center justify-between gap-4 text-neutral-300 typography-body-sm md:justify-start">
            <div>
              {formatTime(startTime)} - {formatTime(finishTime)}
            </div>
            <div>Location: {location}</div>
          </div>
        </div>
        <div className="ml-auto flex gap-4">
          {(type === 'awaiting-confirmation' || type === 'confirmed') && isEditable && isMd && (
            <>
              <Button preset="secondary" size="sm" onClick={handleConfirm}>
                {type === 'awaiting-confirmation' ? 'Confirm' : 'Edit'}
              </Button>
              <Dropdown>
                <DropdownTrigger>
                  <Button preset="tertiary" className="rounded" Icon={IconTripleDots} size="sm" />
                </DropdownTrigger>
                <DropdownContent align="end" side="bottom">
                  {!isRescheduled && (
                    <Tooltip
                      side="bottom"
                      content={isFadSession ? 'FAD session cannot be canceled or deleted.' : ''}
                      delayDuration={0}
                    >
                      <span>
                        <DropdownItem handleClick={handleCancel} disabled={isFadSession}>
                          Cancel session
                        </DropdownItem>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip
                    side="bottom"
                    content={
                      !canBeDeleted && !isFadSession
                        ? 'Scheduled sessions cannot be deleted; cancel and/or reschedule as needed.'
                        : isFadSession
                        ? 'FAD session cannot be canceled or deleted.'
                        : ''
                    }
                    delayDuration={0}
                  >
                    <span>
                      <DropdownItem
                        disabled={!canBeDeleted || isFadSession}
                        handleClick={handleDelete}
                      >
                        Delete session
                      </DropdownItem>
                    </span>
                  </Tooltip>
                </DropdownContent>
              </Dropdown>
            </>
          )}

          {type === 'cancelled' && isMd && (
            <>
              <Button preset="secondary" size="sm" onClick={handleConfirm}>
                Reschedule
              </Button>
              <Tooltip
                side="bottom"
                content={
                  !canBeDeleted && !isFadSession
                    ? 'Scheduled sessions cannot be deleted; cancel and/or reschedule as needed.'
                    : isFadSession
                    ? 'FAD session cannot be canceled or deleted.'
                    : ''
                }
                delayDuration={0}
              >
                <Button
                  preset="tertiary"
                  size="sm"
                  onClick={handleDelete}
                  disabled={!canBeDeleted || isFadSession}
                >
                  Delete
                </Button>
              </Tooltip>
            </>
          )}
        </div>
      </div>
      {!isMd && (
        <Button preset="secondary" size="sm" onClick={handleConfirm} className="mt-4 w-full">
          {type === 'awaiting-confirmation' ? 'Confirm' : 'Edit'}
        </Button>
      )}
      {type === 'confirmed' && goals.length > 0 && !student?.isSeit && (
        <div>
          <div className="mt-4 flex flex-col gap-4 border-t border-t-neutral-800 pt-4 text-neutral-100 typography-loud-sm">
            {goals.map((g) => (
              <div key={g.id} className="flex flex-col md:flex-row md:items-center">
                <div className="w-3/6 break-words text-neutral-100 typography-loud-sm">
                  {g.name}
                </div>
                <ProgressIndicator
                  value={g.progress}
                  max={5}
                  disabled
                  className="mt-4 shrink-0 md:ml-auto md:mt-0"
                />
              </div>
            ))}
          </div>
        </div>
      )}
      {type === 'confirmed' && goals.length > 0 && student?.isSeit && (
        <div className="mt-4 flex flex-col gap-4 border-t border-t-neutral-800 pt-4">
          {goals.map((goal) => (
            <div key={goal.id} className="flex flex-col gap-2">
              <p className="text-neutral-100 typography-loud-sm">{goal.name}</p>
              <div className="flex flex-col gap-2">
                <BillingActivityField
                  type="activity"
                  value={goal.activity?.map(
                    (a) => sessionActivity?.find((s) => s.key === a)?.value || '',
                  )}
                />
                <BillingActivityField
                  type="participation"
                  value={
                    sessionParticipation?.find((s) => s.key === goal.participation)?.value || ''
                  }
                />
                <BillingActivityField
                  type="outcome"
                  value={sessionOutcome?.find((s) => s.key === goal.outcome)?.value || ''}
                />
              </div>
            </div>
          ))}
        </div>
      )}
      {note && (
        <div className="mt-4 rounded-lg border border-neutral-800 px-4 py-3 text-neutral-100 typography-loud-sm">
          {note}
        </div>
      )}
    </Card>
  );
};

export default BillingSingleSession;
